import i18n from 'i18next';
import decode from 'jwt-decode';
import moment from 'moment';
import { getI18n } from 'react-i18next';
import { get, post } from 'helpers/apiHelpers';
import types from 'helpers/constants';
import translationEN from 'locales/en.json';
import translationPL from 'locales/pl.json';
import translationLV from 'locales/lv.json';
import translationRU from 'locales/ru.json';
import { store } from 'store';

const transformBrands = brands =>
  brands.map(brand => ({
    '@id': brand['@id'],
    name: brand.name,
    id: Number(brand['@id'].split('/').pop()),
  }));

export const handleBrandChange = (event, payload) => async dispatch => {
  await dispatch({
    type: types.CHANGE_BRAND,
    payload: {
      brand: payload.brand.id,
    },
  });

  if (payload.refresh) {
    window.location.reload();
  }
};

export const getRoles = () => dispatch =>
  // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
  get('/my-roles').then(data => {
    const fetchedRoles = data['hydra:member'];
    dispatch({
      type: types.UPDATE_MY_ROLES,
      payload: {
        roles: fetchedRoles,
      },
    });
  });

export const login = (username, password) => dispatch =>
  // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
  post('/login_check', { username, password }).then(
    async data => {
      const user = decode(data.token);
      localStorage.setItem('t', data.token);

      try {
        const response = await get(user.id);

        user.brands = transformBrands(response.brands);
        user.statisticsForBrands = transformBrands(
          response.statisticsForBrands
        );

        dispatch({
          type: types.LOGIN,
          payload: {
            user,
            refreshToken: data.refreshToken,
            token: data.token,
            errorMessage: '',
          },
        });

        if (user.brands.length === 0) {
          // eslint-disable-next-line no-console -- FIXME
          console.error('User is not assigned to any brand');
          // eslint-disable-next-line no-throw-literal -- FIXME
          throw { noBrands: true };
        }
        await dispatch(
          handleBrandChange(null, { brand: { id: user.brands[0].id } })
        );
        await dispatch(getRoles());
      } catch {
        localStorage.removeItem('t');
      }
    },
    // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
    error => {
      // eslint-disable-next-line no-throw-literal -- FIXME
      throw { ...error };
    }
  );

export const addUserBrand = brand => dispatch => {
  const {
    Auth: {
      user: { brands },
    },
  } = store.getState();
  const withoutCurrentBrand = brands.filter(({ id }) => id !== brand.id);
  const isInBrands = brands.length !== withoutCurrentBrand.length;
  const newBrands = isInBrands
    ? [...withoutCurrentBrand, brand]
    : [...brands, brand];

  dispatch({
    type: types.ADD_USER_BRAND,
    payload: { brands: newBrands },
  });
};

export const logout = () => dispatch =>
  // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
  get('/logout').then(() => {
    dispatch({
      type: types.LOGOUT,
      payload: {},
    });
  });

export const refreshLogin = () => dispatch =>
  post('/refresh_token', { refreshToken: store.getState().Auth.refreshToken })
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    .then(async data => {
      const user = decode(data.token);
      localStorage.setItem('t', data.token);

      try {
        const response = await get(user.id);

        user.brands = transformBrands(response.brands);
        user.statisticsForBrands = transformBrands(
          response.statisticsForBrands
        );

        await dispatch({
          type: types.REFRESH_LOGIN,
          payload: {
            user,
            refreshToken: data.refreshToken,
            token: data.token,
          },
        });
        await dispatch(getRoles());
      } catch {
        localStorage.removeItem('t');
      }
    })
    // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks, no-return-assign, @typescript-eslint/no-unused-vars -- FIXME
    .catch(err => (window.location.href = '/auth/login'));

export const refreshToken = () => dispatch =>
  post('/refresh_token', { refreshToken: store.getState().Auth.refreshToken })
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    .then(async data => {
      const user = decode(data.token);
      localStorage.setItem('t', data.token);

      try {
        const response = await get(user.id);

        user.brands = transformBrands(response.brands);
        user.statisticsForBrands = transformBrands(
          response.statisticsForBrands
        );

        await dispatch({
          type: types.REFRESH_LOGIN,
          payload: {
            user,
            refreshToken: data.refreshToken,
            token: data.token,
          },
        });

        return data?.token;
      } catch {
        localStorage.removeItem('t');
        window.location.href = '/auth/login';
        return null;
      }
    })
    // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks, no-return-assign, @typescript-eslint/no-unused-vars -- FIXME
    .catch(err => (window.location.href = '/auth/login'));

export const setTemporaryLock = isLock => async dispatch => {
  await dispatch({
    type: types.SET_TEMPORARY_LOCK,
    payload: {
      isLock,
      expiredAt: isLock ? moment().add(5, 'minutes') : moment(),
    },
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, require-await -- FIXME
export const reloadTranslations = () => async dispatch => {
  const DEFAULT_BUNDLES = {
    en: translationEN,
    pl: translationPL,
    lv: translationLV,
    ru: translationRU,
  };
  // eslint-disable-next-line no-async-promise-executor, promise/param-names -- FIXME
  return new Promise(async res => {
    const lang = getI18n().language.substr(0, 2);
    const resource = i18n.getResource(lang, 'translation');

    if (typeof resource !== 'undefined') {
      i18n.removeResourceBundle(lang, 'translation');
    }

    let startedLanguage = {};

    try {
      startedLanguage = await get(
        '/translations/loader/admin/default',
        {},
        { forcedLanguage: lang }
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-empty -- FIXME
    } catch (e) {}
    await i18n.addResourceBundle(lang, 'translation', {
      ...(DEFAULT_BUNDLES[lang ?? i18n.language ?? 'pl'] ?? {}),
      ...startedLanguage,
    });
    res(true);
  });
};

export const fetchLanguages = () => dispatch =>
  // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
  get('languages').then(async response => {
    const languages = response['hydra:member'].filter(
      lang => lang.enabledForAdmin || lang.enabledForClient
    );
    const clientLanguages = response['hydra:member'].filter(
      lang => lang.enabledForClient
    );
    const selectedLanguage =
      languages.find(
        language => language.isoCode === store.getState().Auth.user.language
      ) ?? languages[0];

    await dispatch({
      type: 'SET_LANGUAGES',
      payload: {
        languages,
        clientLanguages,
        selectedLanguage,
      },
    });
  });

export const setSelectedLanguage = selectedLanguage => dispatch => {
  dispatch({
    type: 'SET_SELECTED_LANGUAGE',
    payload: selectedLanguage,
  });
};
